// frontend/src/pages/SkillsByLevelPage.jsx
// 経験録 - レベル別スタッフ一覧
// 診療科 > 術式 > 役割 (器械出し / 外回り / 任意ラベル) ごとに
// 「指導可」「独り立ち」などレベルに該当するスタッフを一覧表示する
//
// 機能:
// - 未登録 (レコード無し) もバケットとして表示
// - 各レベル枠は開閉式 (件数バッジは常時表示)
// - skill_records はページネーションで全件取得
// - 濃色チップは白文字

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { supabase, getStaffList } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";

// 未経験 = レコード無し + level="未経験" を統合 (ユーザー仕様)
const INEXPERIENCED = "未経験";
const LEVELS = [
  "指導可",
  "独り立ち",
  "外から見守り",
  "経験3回以上",
  "経験2回",
  "経験1回",
  "見学済み",
  INEXPERIENCED,
];
const DEFAULT_LEVELS = ["指導可", "独り立ち", INEXPERIENCED];
const LEVEL_COLORS = {
  "見学済み": "#ffe0b2",
  "経験1回": "#ffcc80",
  "経験2回": "#ffb74d",
  "経験3回以上": "#ffa726",
  "外から見守り": "#66bb6a",
  "独り立ち": "#26a69a",
  "指導可": "#00897b",
  [INEXPERIENCED]: "#e2e8f0",
};
const LEVEL_TEXT_COLORS = {
  "見学済み": "#1e293b",
  "経験1回": "#1e293b",
  "経験2回": "#1e293b",
  "経験3回以上": "#1e293b",
  "外から見守り": "#ffffff",
  "独り立ち": "#ffffff",
  "指導可": "#ffffff",
  [INEXPERIENCED]: "#1e293b",
};
const ROLE_LABEL = { scrub: "器械出し", circulating: "外回り" };
const PAGE_SIZE = 1000;

// skill_records を organization_id フィルタでページネーション取得
async function fetchAllSkillRecords(organizationId) {
  const all = [];
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from("skill_records")
      .select("user_id, procedure_id, role, label, level")
      .eq("organization_id", organizationId)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return all;
}

export default function SkillsByLevelPage() {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [skillRecords, setSkillRecords] = useState([]);

  // Filters
  const [deptFilter, setDeptFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all"); // all / scrub / circulating
  const [levelFilter, setLevelFilter] = useState(new Set(DEFAULT_LEVELS));
  const [yearFilter, setYearFilter] = useState("all");
  const [nameSearch, setNameSearch] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      if (!currentOrganization?.id) return;
      setLoading(true);
      setError("");
      try {
        const orgId = currentOrganization.id;
        // staff は edge function 経由で全員取得 (RLS バイパス、他ページと同じ)。
        // skill_records.user_id は staff.id を参照 (他ページ: SkillChart/ProcedureDetailModal/InstructionNotificationModal も同じ)。
        const [staffAll, deptRes, procRes, jobTypeRes, skillAll] = await Promise.all([
          getStaffList(orgId),
          supabase
            .from("departments")
            .select("id, name")
            .eq("organization_id", orgId),
          supabase
            .from("procedures")
            .select("id, name, department_id, required_roles")
            .eq("organization_id", orgId),
          supabase
            .from("job_types")
            .select("id, has_skill_evaluation")
            .eq("organization_id", orgId),
          fetchAllSkillRecords(orgId),
        ]);
        if (deptRes.error) throw deptRes.error;
        if (procRes.error) throw procRes.error;
        if (jobTypeRes.error) throw jobTypeRes.error;

        // スキル評価対象の職種のみ対象 (職種設定の has_skill_evaluation = true)
        const skillTargetJobTypeIds = new Set(
          (jobTypeRes.data || [])
            .filter((j) => j.has_skill_evaluation === true)
            .map((j) => j.id)
        );
        setStaffList(
          (staffAll || [])
            .filter((s) => s.is_active !== false)
            .filter((s) => skillTargetJobTypeIds.has(s.job_type_id))
        );
        setDepartments(deptRes.data || []);
        setProcedures(
          (procRes.data || []).map((p) => ({
            ...p,
            required_roles:
              typeof p.required_roles === "string"
                ? JSON.parse(p.required_roles || "[]")
                : p.required_roles || [],
          }))
        );
        setSkillRecords(skillAll);
      } catch (e) {
        console.error(e);
        setError(e.message || "データ取得失敗");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [currentOrganization?.id]);

  const currentFiscalYear = useMemo(() => {
    const now = dayjs();
    return now.month() >= 3 ? now.year() : now.year() - 1;
  }, []);

  const staffMap = useMemo(() => {
    const m = {};
    staffList.forEach((s) => { m[s.id] = s; });
    return m;
  }, [staffList]);

  // (procedure_id, role, label || '') ごとに、レベル→staff[] と registered Set
  const { bucketed, registeredByKey } = useMemo(() => {
    const buckets = new Map();   // key -> Map(level -> staff[])
    const registered = new Map(); // key -> Set(user_id)
    skillRecords.forEach((r) => {
      const key = `${r.procedure_id}|${r.role}|${r.label || ""}`;
      if (!buckets.has(key)) buckets.set(key, new Map());
      if (!registered.has(key)) registered.set(key, new Set());
      const levelMap = buckets.get(key);
      if (!levelMap.has(r.level)) levelMap.set(r.level, []);
      const staff = staffMap[r.user_id];
      if (staff) {
        levelMap.get(r.level).push(staff);
        registered.get(key).add(r.user_id);
      }
    });
    return { bucketed: buckets, registeredByKey: registered };
  }, [skillRecords, staffMap]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(staffList.map((s) => Number(s.year)).filter((y) => !isNaN(y)))];
    return years.sort((a, b) => a - b);
  }, [staffList]);

  const passStaffFilter = (staff) => {
    if (!staff) return false;
    if (yearFilter !== "all" && Number(staff.year) !== Number(yearFilter)) return false;
    if (nameSearch.trim()) {
      const kw = nameSearch.toLowerCase();
      const fullName = `${staff.last_name || ""}${staff.first_name || ""}`.toLowerCase();
      if (!fullName.includes(kw)) return false;
    }
    return true;
  };

  const toggleLevel = (lv) => {
    setLevelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(lv)) next.delete(lv); else next.add(lv);
      return next;
    });
  };

  const visibleDepartments = useMemo(() => {
    if (deptFilter === "all") return departments;
    return departments.filter((d) => d.id === deptFilter);
  }, [departments, deptFilter]);

  const proceduresByDept = useMemo(() => {
    const m = {};
    procedures.forEach((p) => {
      if (!m[p.department_id]) m[p.department_id] = [];
      m[p.department_id].push(p);
    });
    return m;
  }, [procedures]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">データを読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* ===== ヘッダー ===== */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <button
                onClick={() => navigate("/skills")}
                className="text-sm text-teal-700 hover:underline mb-1"
              >
                ← 経験録トップ
              </button>
              <h1 className="text-2xl font-bold text-gray-800">
                レベル別スタッフ一覧
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                診療科 × 術式 × 役割ごとに、指定したレベルに該当するスタッフを表示します。 枠はクリックで開閉できます。
              </p>
              <div className="flex gap-2 mt-2 text-[11px]">
                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  アクティブスタッフ {staffList.length}
                </span>
                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  術式 {procedures.length}
                </span>
                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  経験録レコード {skillRecords.length}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded mb-3 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-600 mb-2">表示レベル (複数選択可)</div>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((lv) => {
                const on = levelFilter.has(lv);
                return (
                  <button
                    key={lv}
                    onClick={() => toggleLevel(lv)}
                    className="px-3 py-1 rounded-full text-xs font-semibold border transition"
                    style={{
                      background: on ? LEVEL_COLORS[lv] : "#fff",
                      color: on ? LEVEL_TEXT_COLORS[lv] : "#64748b",
                      borderColor: on ? LEVEL_COLORS[lv] : "#cbd5e1",
                    }}
                  >
                    {on ? "✓ " : ""}{lv}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">診療科</label>
              <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="all">全ての診療科</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">役割</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="all">全て</option>
                <option value="scrub">器械出しのみ</option>
                <option value="circulating">外回りのみ</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">入職年</label>
              <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="all">全学年</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {currentFiscalYear - y + 1}年目 ({y}年入職)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">名前検索</label>
              <input type="text" value={nameSearch} onChange={(e) => setNameSearch(e.target.value)}
                placeholder="名前で絞り込み"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
          </div>
        </div>

        {visibleDepartments.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            診療科が登録されていません
          </div>
        )}

        {visibleDepartments.map((dept) => {
          const procs = (proceduresByDept[dept.id] || []).slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"));
          if (procs.length === 0) return null;

          return (
            <DepartmentSection
              key={dept.id}
              dept={dept}
              procedures={procs}
              staffList={staffList}
              bucketed={bucketed}
              registeredByKey={registeredByKey}
              roleFilter={roleFilter}
              levelFilter={levelFilter}
              passStaffFilter={passStaffFilter}
              onStaffClick={(staffId) => navigate(`/skills/chart/${staffId}`)}
            />
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// 計算ヘルパー: (procedure, role) に対する level→staff[] を得る
// =============================================================================
function computeLevelStaff(procedure, role, staffList, bucketed, registeredByKey, levelFilter, passStaffFilter) {
  const key = `${procedure.id}|${role.type}|${role.label || ""}`;
  const levelMap = bucketed.get(key) || new Map();
  const registered = registeredByKey.get(key) || new Set();

  const rows = [];
  for (const lv of LEVELS) {
    if (!levelFilter.has(lv)) continue;
    let list;
    if (lv === INEXPERIENCED) {
      // 未経験 = (レコード level="未経験") + (レコード無し) をマージ (user_id で重複排除)
      const explicit = levelMap.get(lv) || [];
      const seen = new Set(explicit.map((s) => s.id));
      const unregistered = staffList.filter((s) => !registered.has(s.id) && !seen.has(s.id));
      list = [...explicit, ...unregistered].filter(passStaffFilter);
    } else {
      list = (levelMap.get(lv) || []).filter(passStaffFilter);
    }
    if (list.length === 0) continue;
    list.sort((a, b) => Number(a.year || 0) - Number(b.year || 0));
    rows.push({ level: lv, staff: list });
  }
  return rows;
}

// =============================================================================
// Section components
// =============================================================================
function DepartmentSection(props) {
  const { dept, procedures, staffList, bucketed, registeredByKey, roleFilter, levelFilter, passStaffFilter, onStaffClick } = props;

  const anyVisibleProc = procedures.some((p) => {
    const roles = (p.required_roles || []).filter((r) => roleFilter === "all" || r.type === roleFilter);
    const effectiveRoles = roles.length > 0 ? roles
      : (roleFilter === "all" ? [{ type: "scrub" }, { type: "circulating" }] : [{ type: roleFilter }]);
    return effectiveRoles.some((role) => {
      const rows = computeLevelStaff(p, role, staffList, bucketed, registeredByKey, levelFilter, passStaffFilter);
      return rows.some((r) => r.staff.length > 0);
    });
  });
  if (!anyVisibleProc) return null;

  return (
    <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-3 text-white">
        <h2 className="text-lg font-bold">{dept.name}</h2>
      </div>
      <div className="divide-y">
        {procedures.map((p) => (
          <ProcedureRow
            key={p.id}
            procedure={p}
            staffList={staffList}
            bucketed={bucketed}
            registeredByKey={registeredByKey}
            roleFilter={roleFilter}
            levelFilter={levelFilter}
            passStaffFilter={passStaffFilter}
            onStaffClick={onStaffClick}
          />
        ))}
      </div>
    </div>
  );
}

function ProcedureRow({ procedure, staffList, bucketed, registeredByKey, roleFilter, levelFilter, passStaffFilter, onStaffClick }) {
  const required = (procedure.required_roles || []).filter((r) => roleFilter === "all" || r.type === roleFilter);
  const effectiveRoles = required.length > 0 ? required
    : (roleFilter === "all" ? [{ type: "scrub" }, { type: "circulating" }] : [{ type: roleFilter }]);

  const rowData = effectiveRoles.map((role) => ({
    role,
    levelRows: computeLevelStaff(procedure, role, staffList, bucketed, registeredByKey, levelFilter, passStaffFilter),
  }));

  const hasAny = rowData.some((r) => r.levelRows.some((lr) => lr.staff.length > 0));
  if (!hasAny) return null;

  return (
    <div className="px-6 py-4">
      <div className="font-semibold text-gray-800 mb-2">{procedure.name}</div>
      <div className="space-y-3">
        {rowData.map(({ role, levelRows }, idx) => {
          const roleLabel = (ROLE_LABEL[role.type] || role.type) + (role.label ? ` (${role.label})` : "");
          const nonEmptyRows = levelRows.filter((lr) => lr.staff.length > 0);
          if (nonEmptyRows.length === 0) return null;
          return (
            <div key={`${role.type}_${role.label || ""}_${idx}`} className="flex flex-col sm:flex-row gap-2 sm:items-start">
              <div className="sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-1">
                {roleLabel}
              </div>
              <div className="flex-1 flex flex-wrap gap-2">
                {nonEmptyRows.map(({ level, staff }) => (
                  <LevelBox
                    key={level}
                    level={level}
                    staff={staff}
                    onStaffClick={onStaffClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LevelBox({ level, staff, onStaffClick }) {
  const [open, setOpen] = useState(false);
  const bg = LEVEL_COLORS[level];
  const fg = LEVEL_TEXT_COLORS[level];

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: bg }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full px-2 py-1 text-left transition hover:opacity-90"
        style={{ background: bg, color: fg }}
      >
        <span className="text-[10px] font-bold uppercase tracking-wide">
          {level}
        </span>
        <span className="text-xs font-bold">
          ({staff.length})
        </span>
        <span className="ml-auto text-xs opacity-80">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <div className="p-2 bg-white flex flex-wrap gap-1">
          {staff.map((s) => (
            <button
              key={s.id}
              onClick={() => onStaffClick(s.id)}
              className="text-xs bg-white hover:bg-gray-100 border border-gray-200 rounded px-2 py-0.5 transition"
              title={`${s.employee_id || ""} / ${s.year || ""}年入職`}
            >
              {s.last_name} {s.first_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
